import { Application, FeathersService, Paginated, Query } from "@feathersjs/feathers";
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { computedFn } from "mobx-utils";
import { DataType, ItemType, LoadingStatus } from "src/types";
import { deepCmp } from "src/util/util";
import { BaseStore, RootStore } from ".";
import client from "../feathers";
import { Data } from "../models";
import { IData } from "../types";
import { DataContainer } from "./dataContainer";

export type EventHookType     = "before" | "after" | "error";
export type EventHookMethod   = "created" | "removed" | "updated" | "patched";
export type EventHook<I>      = Record<"all" | EventHookMethod | "finally", ((data: I, err?: Error) => any)[]>;
export type EventHookChain<I> = Record<EventHookType, EventHook<I>>;


/**
 * Base class for all data stores. The stores provides basic functionality to 
 * load and maintain data of type @T in its @container. Event listeners 
 * can be enabled to keep this data up to date with the database. Eventlisteners
 * have an event hook chain that allows inserting callbacks into these updates
 * (see @executeHookChain for more information).
 * 
 * The store provides an @active element from the container. This is the item that is 
 * currently edited. The assumption is that among all data stores there is at most one 
 * item that is edited at a time. Setting the @active element creates a copy of that element.
 * Changes to this element can then be stored to the database and this triggers update events. 
 * Before these events are processed the container is not updated. So changes to the active
 * element can be discarded by not saving. 
 * 
 * The @storage can be used to hold temporary data of various types. While the main container
 * persists data and subjects it to realtime updates, the data in here can be requested and
 * as needed. While DataContainers in @storage can be updated, they are generally not subject to
 * realtime updates. 
 */
export class BaseDataStore<T extends Data<I> & I, I extends IData, C extends string = string> extends BaseStore {
  private   _loadId: number = 0;
  protected service: FeathersService<Application<I>, any> = client.service(this.data_type as ItemType);
  protected readonly pageSize: number = 200;

  protected eventCallBacks: EventHookChain<I> = {
    "before": { "all": [],"created": [], "removed": [], "updated": [], "patched": [], "finally": [] },
    "after":  { "all": [],"created": [], "removed": [], "updated": [], "patched": [], "finally": [] },
    "error":  { "all": [],"created": [], "removed": [], "updated": [], "patched": [], "finally": [] },
  }

  activeStack?:   T[] = [];
  container: {[id: number]: T} = {};
  status:     LoadingStatus = LoadingStatus.None;
  storage:   Partial<Record<C, DataContainer<any>>> = {};

  constructor(
    protected readonly rootStore: RootStore, 
    protected readonly create: (data?: I) => T, 
    protected readonly data_type: DataType, 
    protected _registerEventListener: boolean = true, 
    protected _updateActive:          boolean = true
    ){
      super();

      // Note: This will not take care of async calls. So everything that might be called async has to be wrapped in runInAction() to avoid warnings 
      makeObservable(this, {
        activeStack: observable,
        container:   observable,
        status:      observable,

        getItems:   computed,
        getActive:  computed,
        hasChanged: computed,
        isLoading:  computed,

        setActive:     action,
        setActiveById: action,
        clear:         action,
      }); 
      this.rootStore = rootStore;

      // Register event listener:
      if(_registerEventListener) this.registerEventListener();
  }
  init() {}
  
  /*******************************************************************************************************************************************
   * DB Calls 
   ********************************************************************************************************************************************/
  /**
   * Executes the event hook chain for the given @type and @method. The update event data is in @data
   * The flow of the chain is 
   * 
   * before.all > before.method > before.finally > update store container > after.all > after.method > after.finally
   * 
   * If at any point an error is thrown, the hook chain is canceled and the error hooks are called instead in the order
   * with @err set to the error.
   * 
   * error.all > error.method > error.finally
   * 
   * 
   * 
   * @param data   update event data
   * @param type   hook type before, after, error
   * @param method method of the event, created, removed, patched, updated. Note that "all" and "finally" are called automatically 
   * @param err    error that was thrown inside one of the hooks
   */
  protected executeHookChain = async (data: I, type: EventHookType, method: EventHookMethod, err?: Error ): Promise<void> => {
    if(err && type === "error" && 
        !this.eventCallBacks[type].all.length &&
        !this.eventCallBacks[type].finally.length &&
        !this.eventCallBacks[type][method].length) {
      throw err;
    }

    for(const clb of this.eventCallBacks[type].all)     clb(data, err);
    for(const clb of this.eventCallBacks[type][method]) clb(data, err);
    for(const clb of this.eventCallBacks[type].finally) clb(data, err);
  }
  /**
    * Registers event listeners for CREATE, REMOVE and UPDATE events of @service . The data is updated in @container by using 
    * @conversion to convert from the database representation to the local representation of the data. 
    * After @container is updated the event callback queue @eventCallBacks of the corresponding event is called. 
    */
  protected registerEventListener = () => {
    this.service.on("removed", async (data: I) => {
      try {
        await this.executeHookChain(data, "before", "removed");
        runInAction(() => delete this.container[data.id]);
        await this.executeHookChain(data, "after", "removed");
      } catch(err) {
        await this.executeHookChain(data, "error", "removed", err);
      }
    });
    this.service.on("created", async (data: I) => {
      try {
        await this.executeHookChain(data, "before", "created");
        runInAction(() => this.container[data.id] = this.create(data));
        if(this._updateActive) this.updateActive(data);
        await this.executeHookChain(data, "after", "created");
      } catch(err) {
        await this.executeHookChain(data, "error", "created", err);
      }
    });
    
    ["updated", "patched"].forEach((method: EventHookMethod) => {      
      this.service.on(method, async (data: I) => {
        try {
          await this.executeHookChain(data, "before", method);
          runInAction(() => {
            if(this.container[data.id]) {
              this.container[data.id].patch(data);
              this.container = Object.assign({}, this.container);
            } else this.container[data.id] = this.create(data);
          });
          if(this._updateActive) this.updateActive(data);
          await this.executeHookChain(data, "after", method);
        } catch(err) {
          await this.executeHookChain(data, "error", method, err);
        }
      });
    });
  }

  /**
   * Load items from the database with a FIND request. 
   * 
   * @queryOptions the base query option is only ' $limit: 1000'. More options can be found here:
   *               https://docs.feathersjs.com/api/databases/querying.html
   * @service      Feathers service that the FIND request is made to
   * @container    member of the DataStore that will hold the returned data
   * @callBack     this is called for each data item that is returned
   */
  protected loadItems = async <M, J extends IData = I>(
      queryOptions: Query   = {},
      paginate:     boolean = false,
      page:         number  = 0,
      callBack?:    (data: I | J) => void,
  ): Promise<void> => {
      const limit: number = queryOptions["$limit"] || this.pageSize;
      let data:  Paginated<I | J>;

      // Make sure that only latest load of colliding requests is executed
      this._loadId = (this._loadId+1) % 10000;
      const loadId = this._loadId;
      runInAction(() => this.status = LoadingStatus.Loading);
      try {
        data = (await this.service.find(queryOptions["query"] ? queryOptions : {
          query: {
              $limit: limit,
              $skip:  page * limit,
              ...queryOptions
          }
        // The Service method claims it returns T | T[] but by default pagination is enabled.
        // That means the returned object is wrapped in a pagination object. 
        })) as unknown as Paginated<I | J>;
      } catch (e) {
        // @ts-ignore Type definition doesnt contain the path prop
        console.error("Data load failed", this.service.path, e);
        return;
      }

      if(loadId !== this._loadId) return;

      runInAction(() => {
          if(!page) Object.keys(this.container).forEach((k: string) => delete this.container[Number(k)])
          this.container = Object.assign({}, this.container, data.data.reduce((res: {[key: number]: T | M}, val) => {
              callBack && callBack(val);
              res[val.id] = this.create(val as I);
              return res;
          }, {}));
      });

      // console.log("paginate", paginate, "limit", limit, "page", page)
      if(!paginate && (page+1)*limit < data.total) {
        runInAction(() => this.status = LoadingStatus.Next);
        this.loadItems(queryOptions, true, page+1, callBack);
      } else runInAction(() => this.status = LoadingStatus.Finished);
  }

  protected getCurFilter = (): Query => ({}); // TODO this turns out to be rather useless.... remove that eventually

  loadStorage = (container: C) => {
    if(!this.storage[container]) {
      console.error("Store does not have this container", this.data_type, container)
      return;
    }
    this.storage[container].load();
  }

  /*******************************************************************************************************************************************
   * Getter 
   ********************************************************************************************************************************************/

  /**
   * @return all items regardless of type
   */
  get getItems(): T[] {
    return Object.values(this.container);
  }

  /**
   * @return item with ID @id
   */
  getItemByID = computedFn((id: number): T => {
      return this.container[id];
  });

  getItemsByProp = computedFn((prop: keyof I, value: any) => {
    return Object.values(this.container).filter(i => i[prop] === value);
  })

  get getActive(): T | null {
    return this.activeStack.length ? this.activeStack[this.activeStack.length-1] : null;
  }

  /**
   * @return true if the active item and the corresponding container items are different. 
   */
  get hasChanged(): boolean {
    if(!this.getActive) return false;
    if(this.getActive && !this.getActive.id) return true;
    return deepCmp(this.getActive, this.container[this.getActive.id]);
  }

  get isLoading(): boolean {
    return this.status !== LoadingStatus.Finished
  }

  /*******************************************************************************************************************************************
   * Setter 
   ********************************************************************************************************************************************/

  setActive(a?: I, flush?: boolean): void {
    if(flush) this.activeStack = [];
    if(a) this.activeStack.push(this.create(a));
    else this.activeStack.pop();
  }

  async setActiveById(id: number | string): Promise<void> {
    if(this.container[Number(id)]) this.setActive(id ? this.container[Number(id)] : null);
    else this.setActive(await this.service.get(id));
  }

  /**
 * Updates a container entry and forces mobx to react to the update. 
 * 
 * @param container Container of type {[id: number]: {[key: string]: any}}
 * @param key       Key of the entry that changes
 * @param prop      Prop name of the entry that changes
 * @param value     New value for @prop of the entry that chages
 */
  protected updateContainerEntry = (key: number, prop: keyof I, value: any): void => {
    runInAction(() => {
      this.container[key][prop] = value;
      this.container = Object.assign({}, this.container);
    });
  }

  /*******************************************************************************************************************************************
   * Actions
   ********************************************************************************************************************************************/
  clear() {
    this.setActive(null);
    Object.keys(this.container).forEach((k: string) => delete this.container[Number(k)]);
    Object.values(this.storage).forEach((s: DataContainer<any>) => s.clear());
  }

  /**
   * Called by updated and patched event listener. Checks if the updated 
   * item is set as active item and applies the retrieved data as patch.
   * 
   * Note: Cannot use arrow function here since otherwise super calls dont work
   */
  protected updateActive(data: I): void {
    if(this.getActive && this.getActive.id === Number(data.id)) this.getActive.patch(data);
  }
}

